import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday,
  parseISO,
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const STATUS_COLORS = {
  inquiry: 'border-l-4 border-l-red-500 bg-red-500/15 text-red-400',
  tentative: 'border-l-4 border-l-yellow-500 bg-yellow-500/15 text-yellow-400',
  confirmed: 'border-l-4 border-l-emerald-500 bg-emerald-500/15 text-emerald-400',
  completed: 'border-l-4 border-l-cyan-500 bg-cyan-500/15 text-cyan-400',
  cancelled: 'border-l-4 border-l-zinc-500 bg-zinc-500/15 text-zinc-400 line-through opacity-60',
};

const CalendarPage = () => {
  usePageTitle('Calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCancelled, setShowCancelled] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsAPI.list({ limit: 1000 }); // Get all events for calendar view
      // Handle new paginated response format
      if (response.data.data && response.data.pagination) {
        setEvents(response.data.data);
      } else {
        setEvents(response.data);
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Get calendar days including padding from previous/next months
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Filter events by status and cancelled toggle
  const filteredEvents = events.filter(e => {
    // First check cancelled filter
    if (e.status === 'cancelled' && !showCancelled) return false;
    // Then check status filter
    if (statusFilter === 'all') return true;
    return e.status === statusFilter;
  });

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return filteredEvents.filter(event => {
      if (!event.start_date) return false;
      const startDate = parseISO(event.start_date);
      const endDate = event.end_date ? parseISO(event.end_date) : startDate;
      return day >= startDate && day <= endDate;
    });
  };

  // Check if an event starts on this day
  const isEventStart = (event, day) => {
    if (!event.start_date) return false;
    return isSameDay(parseISO(event.start_date), day);
  };

  // Check if an event ends on this day
  const isEventEnd = (event, day) => {
    if (!event.end_date && !event.start_date) return false;
    const endDate = event.end_date ? parseISO(event.end_date) : parseISO(event.start_date);
    return isSameDay(endDate, day);
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <CalendarIcon className="h-8 w-8 text-amber-400" />
            Event Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            View all events across the timeline
          </p>
        </div>
      </div>

      {/* Calendar Controls */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold min-w-[200px] text-center">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <Button variant="outline" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} className="ml-2">
                Today
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Filter:</span>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px] bg-background/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="inquiry">Inquiry</SelectItem>
                    <SelectItem value="tentative">Tentative</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-cancelled"
                  checked={showCancelled}
                  onCheckedChange={setShowCancelled}
                />
                <Label 
                  htmlFor="show-cancelled" 
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Show cancelled
                </Label>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="border border-border/50 rounded-lg overflow-hidden">
            {/* Week day headers */}
            <div className="grid grid-cols-7 bg-muted/30">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-medium text-muted-foreground border-b border-border/50"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isCurrentDay = isToday(day);

                return (
                  <div
                    key={idx}
                    className={`min-h-[120px] p-1 border-b border-r border-border/30 ${
                      !isCurrentMonth ? 'bg-muted/20' : 'bg-background/30'
                    } ${isCurrentDay ? 'bg-amber-500/10' : ''}`}
                  >
                    <div className={`text-sm p-1 ${
                      isCurrentDay 
                        ? 'bg-amber-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold' 
                        : isCurrentMonth 
                          ? 'text-foreground' 
                          : 'text-muted-foreground/50'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-3 mt-1">
                      {dayEvents.slice(0, 3).map((event) => {
                        const isStart = isEventStart(event, day);
                        const isMultiDay = event.end_date && event.start_date !== event.end_date;
                        const isContinuation = !isStart && isMultiDay;
                        
                        return (
                          <Link
                            key={`${event.id}-${format(day, 'yyyy-MM-dd')}`}
                            to={`/events/${event.id}`}
                            className={`block text-xs p-1.5 rounded border border-border/50 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all ${
                              STATUS_COLORS[event.status] || STATUS_COLORS.inquiry
                            } ${isContinuation ? '!border-l-0' : ''}`}
                            title={`${event.event_name}${event.flex_number ? ` (#${event.flex_number})` : ''} - ${event.status}`}
                          >
                            <div className="font-medium truncate">
                              {isContinuation && <span className="opacity-60 mr-1">↳</span>}
                              {event.event_name}
                            </div>
                            {event.flex_number && (
                              <div className="text-[10px] opacity-70 truncate">
                                #{event.flex_number}
                              </div>
                            )}
                          </Link>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-muted-foreground pl-1 font-medium">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-border/50">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Status:</span>
              {Object.entries({
                inquiry: 'Inquiry',
                tentative: 'Tentative', 
                confirmed: 'Confirmed',
                completed: 'Completed',
                cancelled: 'Cancelled'
              }).map(([status, label]) => (
                <div key={status} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-sm ${STATUS_COLORS[status]}`} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 border-l border-border/50 pl-6">
              <span className="text-sm text-muted-foreground">Multi-day:</span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">↳ = continuation day</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events List */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Events This Month</h3>
          {filteredEvents.filter(event => {
            if (!event.start_date) return false;
            const startDate = parseISO(event.start_date);
            const endDate = event.end_date ? parseISO(event.end_date) : startDate;
            return (startDate >= monthStart && startDate <= monthEnd) ||
                   (endDate >= monthStart && endDate <= monthEnd) ||
                   (startDate <= monthStart && endDate >= monthEnd);
          }).length > 0 ? (
            <div className="space-y-2">
              {filteredEvents
                .filter(event => {
                  if (!event.start_date) return false;
                  const startDate = parseISO(event.start_date);
                  const endDate = event.end_date ? parseISO(event.end_date) : startDate;
                  return (startDate >= monthStart && startDate <= monthEnd) ||
                         (endDate >= monthStart && endDate <= monthEnd) ||
                         (startDate <= monthStart && endDate >= monthEnd);
                })
                .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
                .map((event) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={STATUS_COLORS[event.status] || STATUS_COLORS.inquiry}>
                        {event.status}
                      </Badge>
                      <div>
                        <p className="font-medium">
                          {event.flex_number && (
                            <span className="text-muted-foreground mr-2">#{event.flex_number}</span>
                          )}
                          {event.event_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {event.start_date && format(parseISO(event.start_date), 'MMM d')}
                          {event.end_date && event.end_date !== event.start_date && (
                            <> - {format(parseISO(event.end_date), 'MMM d')}</>
                          )}
                          {event.venue_name && <> • {event.venue_name}</>}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-6">
              No events scheduled for this month
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
